import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Movie, WatchParty } from '../../types';
import { io, Socket } from 'socket.io-client';
import Hls from 'hls.js';
import { Room, RoomEvent, RemoteParticipant } from 'livekit-client';
import './WatchPartyRoom.css';

const WS_URL = 'https://cineclubs.cloud';
const LIVEKIT_URL = 'wss://cineclubs.cloud:7882';

interface ChatMsg { id: number; username: string; message: string; type: 'message' | 'system'; }
interface Person { id: number; user_id: number; username: string; is_host: boolean; }
interface Peer { identity: string; videoTrack: any; audioTrack: any; }

const WatchPartyRoom: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [party, setParty] = useState<WatchParty | null>(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [participants, setParticipants] = useState<Person[]>([]);
  const [movies, setMovies] = useState<Movie[]>([]);
  const [showMovieModal, setShowMovieModal] = useState(false);
  const [movieSearch, setMovieSearch] = useState('');
  const [hlsUrl, setHlsUrl] = useState<string | null>(null);
  const [isHost, setIsHost] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [peers, setPeers] = useState<Map<string, Peer>>(new Map());

  const socketRef = useRef<Socket | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const roomRef = useRef<Room | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const connectingRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) navigate(`/login?redirect=/room/${code}`);
  }, [isAuthenticated, code, navigate]);

  const loadParty = useCallback(async () => {
    if (!code) return;
    try {
      const res = await fetch(`/api/watch-parties/${code}`);
      const data = await res.json();
      if (data.status === 'success' && data.data) {
        setParty(data.data);
        setParticipants(data.data.participants || []);
        const me = data.data.participants?.find((p: Person) => p.user_id === user?.id);
        setIsHost(me?.is_host || false);
        if (data.data.video_url) loadVideo(data.data.video_url);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [code, user?.id]);

  const loadVideo = (url: string) => {
    setHlsUrl(url);
    if (!videoRef.current) return;
    if (url.includes('.m3u8') && Hls.isSupported()) {
      hlsRef.current?.destroy();
      const hls = new Hls();
      hls.loadSource(url);
      hls.attachMedia(videoRef.current);
      hlsRef.current = hls;
    } else {
      videoRef.current.src = url;
    }
  };

  const connectSocket = useCallback(() => {
    if (!code || !user || socketRef.current) return;
    const socket = io(WS_URL, { path: '/socket.io/', transports: ['websocket', 'polling'] });
    socketRef.current = socket;
    socket.on('connect', () => socket.emit('join', { roomCode: code, userId: user.id, username: user.username, isHost }));
    socket.on('chat_message', (d: ChatMsg) => setMessages(p => [...p, d]));
    socket.on('user_joined', (d: { username: string }) => setMessages(p => [...p, { id: Date.now(), username: '', message: `${d.username} joined`, type: 'system' }]));
    socket.on('user_left', (d: { username: string }) => setMessages(p => [...p, { id: Date.now(), username: '', message: `${d.username} left`, type: 'system' }]));
    socket.on('movie_selected', (d: { videoUrl: string; movieTitle: string }) => {
      loadVideo(d.videoUrl);
      setMessages(p => [...p, { id: Date.now(), username: '', message: `Now playing: ${d.movieTitle}`, type: 'system' }]);
    });
  }, [code, user, isHost]);

  const connectLiveKit = useCallback(async () => {
    if (!code || !user || roomRef.current || connectingRef.current) return;
    connectingRef.current = true;
    try {
      const res = await fetch('/api/livekit-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room: `party_${code}`, identity: user.username, publisher: true })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);

      const room = new Room({ adaptiveStream: true, dynacast: true });
      roomRef.current = room;

      room.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
        setPeers(prev => {
          const m = new Map(prev);
          const e = m.get(participant.identity) || { identity: participant.identity, videoTrack: null, audioTrack: null };
          if (track.kind === 'video') m.set(participant.identity, { ...e, videoTrack: track });
          else if (track.kind === 'audio') {
            m.set(participant.identity, { ...e, audioTrack: track });
            const a = document.createElement('audio'); a.autoplay = true; track.attach(a); document.body.appendChild(a);
          }
          return m;
        });
      });

      room.on(RoomEvent.TrackUnsubscribed, (track, pub, participant) => {
        setPeers(prev => {
          const m = new Map(prev);
          const e = m.get(participant.identity);
          if (e) {
            if (track.kind === 'video') m.set(participant.identity, { ...e, videoTrack: null });
            if (track.kind === 'audio') m.set(participant.identity, { ...e, audioTrack: null });
          }
          return m;
        });
      });

      room.on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
        setPeers(prev => { const m = new Map(prev); m.set(p.identity, { identity: p.identity, videoTrack: null, audioTrack: null }); return m; });
      });

      room.on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
        setPeers(prev => { const m = new Map(prev); m.delete(p.identity); return m; });
      });

      await room.connect(data.url || LIVEKIT_URL, data.token);
      room.remoteParticipants.forEach(p => {
        setPeers(prev => { const m = new Map(prev); m.set(p.identity, { identity: p.identity, videoTrack: null, audioTrack: null }); return m; });
      });
    } catch (e) { console.error(e); connectingRef.current = false; }
  }, [code, user]);

  const toggleCamera = async () => {
    const room = roomRef.current; if (!room) return;
    try {
      await room.localParticipant.setCameraEnabled(!cameraOn);
      setCameraOn(!cameraOn);
      if (!cameraOn && localVideoRef.current) {
        const t = Array.from(room.localParticipant.videoTrackPublications.values())[0]?.track;
        if (t) t.attach(localVideoRef.current);
      }
    } catch (e) { console.error(e); }
  };

  const toggleMic = async () => {
    const room = roomRef.current; if (!room) return;
    try { await room.localParticipant.setMicrophoneEnabled(!micOn); setMicOn(!micOn); } catch (e) { console.error(e); }
  };

  const sendChat = () => {
    if (!chatInput.trim() || !socketRef.current) return;
    socketRef.current.emit('chat_message', { roomCode: code, message: chatInput.trim(), username: user?.username });
    setChatInput('');
  };

  const loadMovies = async () => {
    try {
      const res = await fetch('/api/movies?limit=50');
      const data = await res.json();
      if (data.status === 'success') {
        const list = Array.isArray(data.data) ? data.data : (data.data?.movies || []);
        setMovies(list);
      }
    } catch (e) { console.error(e); }
  };

  const selectMovie = (m: Movie) => {
    if (!socketRef.current || !m.video_url) return;
    socketRef.current.emit('movie_selected', { roomCode: code, videoUrl: m.video_url, movieTitle: m.title });
    setShowMovieModal(false);
  };

  useEffect(() => {
    loadParty(); loadMovies(); connectSocket();
    return () => { roomRef.current?.disconnect(); socketRef.current?.disconnect(); hlsRef.current?.destroy(); };
  }, [loadParty, connectSocket]);

  useEffect(() => { if (party && user) connectLiveKit(); }, [party, user, connectLiveKit]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const filteredMovies = movies.filter(m => m.title.toLowerCase().includes(movieSearch.toLowerCase()));

  if (!isAuthenticated) return null;
  if (loading) return <div className="watch-party-room"><div className="wp-loading"><div className="wp-spinner"></div><p>Loading...</p></div></div>;

  return (
    <div className="watch-party-room">
      {/* Header */}
      <header className="wp-header">
        <div className="wp-header-left">
          <button className="wp-back-btn" onClick={() => navigate('/watchparty')}><i className="fas fa-arrow-left"></i></button>
          <div className="wp-room-info">
            <h1>{party?.title || 'Watch Party'}</h1>
            <div className="wp-room-code">Room: {code}</div>
          </div>
        </div>
        <div className="wp-header-right">
          {isHost && <button className="wp-btn wp-btn-primary" onClick={() => setShowMovieModal(true)}><i className="fas fa-film"></i> Movie</button>}
          <button className="wp-btn wp-btn-ghost" onClick={() => navigator.clipboard.writeText(`${window.location.origin}/room/${code}`)}><i className="fas fa-link"></i></button>
          <button className="wp-btn wp-btn-ghost" onClick={() => navigate('/watchparty')}><i className="fas fa-sign-out-alt"></i></button>
        </div>
      </header>

      <main className="wp-main">
        {/* Movie */}
        <section className="wp-movie-section">
          {hlsUrl ? (
            <video ref={videoRef} controls autoPlay playsInline />
          ) : (
            <div className="wp-no-movie">
              <i className="fas fa-film"></i>
              <p>No movie selected</p>
              {isHost && <button className="wp-btn wp-btn-primary" onClick={() => setShowMovieModal(true)}>Select Movie</button>}
            </div>
          )}
        </section>

        {/* Camera Strip */}
        <div className="wp-camera-strip">
          <div className="wp-camera-controls">
            <button className={`wp-ctrl-btn ${cameraOn ? 'on' : 'off'}`} onClick={toggleCamera}>
              <i className={`fas fa-${cameraOn ? 'video' : 'video-slash'}`}></i>
            </button>
            <button className={`wp-ctrl-btn ${micOn ? 'on' : 'off'}`} onClick={toggleMic}>
              <i className={`fas fa-${micOn ? 'microphone' : 'microphone-slash'}`}></i>
            </button>
          </div>
          <div className="wp-cameras">
            {/* Local */}
            <div className="wp-cam local">
              <video ref={localVideoRef} autoPlay muted playsInline />
              {!cameraOn && <div className="wp-cam-avatar"><span>{user?.username?.[0]?.toUpperCase() || 'U'}</span></div>}
              <div className="wp-cam-name">You</div>
            </div>
            {/* Remote */}
            {Array.from(peers.values()).map(p => (
              <div key={p.identity} className="wp-cam">
                <video autoPlay playsInline ref={el => { if (el && p.videoTrack) p.videoTrack.attach(el); }} />
                {!p.videoTrack && <div className="wp-cam-avatar"><span>{p.identity[0]?.toUpperCase()}</span></div>}
                <div className="wp-cam-name">{p.identity.split('@')[0]}</div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Chat Toggle */}
      <button className="wp-chat-toggle" onClick={() => setChatOpen(!chatOpen)}>
        <i className={`fas fa-${chatOpen ? 'times' : 'comment'}`}></i>
      </button>

      {/* Chat Drawer */}
      <div className={`wp-chat-drawer ${chatOpen ? 'open' : ''}`}>
        <div className="wp-chat-header">
          <h3>Chat</h3>
          <button className="wp-chat-close" onClick={() => setChatOpen(false)}><i className="fas fa-times"></i></button>
        </div>
        <div className="wp-messages">
          {messages.map(m => (
            <div key={m.id} className={`wp-msg ${m.type}`}>
              {m.type === 'message' ? (
                <>
                  <div className="wp-msg-avatar">{m.username[0]?.toUpperCase()}</div>
                  <div className="wp-msg-body">
                    <div className="wp-msg-user">{m.username}</div>
                    <div className="wp-msg-text">{m.message}</div>
                  </div>
                </>
              ) : (
                <div className="wp-msg-text">{m.message}</div>
              )}
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        <div className="wp-chat-input">
          <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && sendChat()} placeholder="Message..." />
          <button onClick={sendChat}><i className="fas fa-paper-plane"></i></button>
        </div>
      </div>

      {/* Movie Modal */}
      {showMovieModal && (
        <div className="wp-modal-bg" onClick={() => setShowMovieModal(false)}>
          <div className="wp-modal" onClick={e => e.stopPropagation()}>
            <div className="wp-modal-head">
              <h2>Select Movie</h2>
              <button className="wp-modal-close" onClick={() => setShowMovieModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="wp-modal-search">
              <input value={movieSearch} onChange={e => setMovieSearch(e.target.value)} placeholder="Search..." />
            </div>
            <div className="wp-modal-list">
              {filteredMovies.map(m => (
                <div key={m.id} className="wp-movie-item" onClick={() => selectMovie(m)}>
                  <img className="wp-movie-thumb" src={m.thumbnail || '/placeholder.jpg'} alt={m.title} />
                  <div className="wp-movie-info">
                    <h4>{m.title}</h4>
                    <p>{m.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WatchPartyRoom;
